import { Injectable } from '@angular/core';
import { BotidService } from '@shared-angular/services/ragai/botid.service';
import { supervisorKeySchema, supervisorKeyValueSchema, supervisorNotifySchema } from '@shared/ragai.schemas';
import { z } from 'zod';
import { BotService } from './bot.service';
import { KeyValService } from './key-val.service';

/**
 * Benachrichtigt den Supervisor und verwaltet Supervisor-Zusatzinformationen (`RAGAI.SUPERVISOR`).
 * @remarks
 * {@link setInfo}/{@link getInfo} sind dünne Wrapper um `RAGAI.KEYVAL` mit fest verdrahtetem Typ `"supervisor"` -
 * es handelt sich also um denselben Speicher wie `RAGAI.KEYVAL`, nur unter einem eigenen Namespace, um
 * Kollisionen mit anderen KeyVal-Einträgen zu vermeiden. {@link notify} löst serverseitig eine
 * Supervisor-Hilfeanfrage für eine konkrete Verlaufsnachricht aus (sichtbar im Supervisor-/Admin-Bereich).
 */
@Injectable({
  providedIn: 'root'
})
export class SupervisorManagerService {

  constructor(
    private botService: BotService,
    private keyValService: KeyValService,
    private botIdService: BotidService
  ) { }

  /**
   * Benachrichtigt den Supervisor, dass eine Nachricht Hilfe benötigt.
   * @param messageName Name/Typ der Benachrichtigung, oder ein Objekt gemäß `supervisorNotifySchema`.
   * @param historyItemId ID der betroffenen Verlaufsnachricht.
   * @returns Nichts.
   */
  public async notify(messageName: string | z.input<typeof supervisorNotifySchema>, historyItemId: string = ""): Promise<void> {
    const parsed = supervisorNotifySchema.safeParse(messageName);
    if (parsed.success && typeof messageName === 'object') {
      messageName = parsed.data.messageName;
    }
    await this.botService.supervisorNotification(this.botIdService.getBotId(), historyItemId, messageName as string);
  }

  /**
   * Speichert eine Zusatzinformation für den Supervisor.
   * @param key Eindeutiger Schlüssel, oder ein Objekt gemäß `supervisorKeyValueSchema`.
   * @param value Der zu speichernde Wert.
   * @returns Der gespeicherte Eintrag.
   */
  public setInfo(key: string | z.input<typeof supervisorKeyValueSchema>, value?: unknown) {
    const parsed = supervisorKeyValueSchema.safeParse(key);
    if (parsed.success && typeof key === 'object') {
      value = parsed.data.value ?? value;
      key = parsed.data.key;
    }
    return this.keyValService.storeKeyValEntry(key, value, "supervisor");
  }

  /**
   * Liest eine zuvor gespeicherte Supervisor-Zusatzinformation.
   * @param key Der gesuchte Schlüssel, oder ein Objekt gemäß `supervisorKeySchema`.
   * @returns Der gespeicherte Wert, oder `null`.
   */
  public async getInfo(key: string | z.input<typeof supervisorKeySchema>) {
    const parsed = supervisorKeySchema.safeParse(key);
    if (parsed.success && typeof key === 'object') {
      key = parsed.data.key;
    }
    return (await this.keyValService.getKeyValByType("supervisor")).find(obj => obj.key === (key as string))?.value ?? null;
  }
}
